/** Utilidades sin JSX. Viven aparte para no romper el fast refresh: un módulo
 *  que exporta componentes y además constantes o funciones pierde el
 *  recargado en caliente de React. */

export function cx(...clases: (string | false | null | undefined)[]): string {
  return clases.filter(Boolean).join(' ')
}

/** Costo en dólares con la precisión que corresponde a su magnitud. */
export function formatearCosto(costo: number | null | undefined): string {
  if (costo === null || costo === undefined) return '—'
  return costo < 0.01 ? `$${costo.toFixed(4)}` : `$${costo.toFixed(2)}`
}

/** Fecha y hora en formato chileno, para las tablas de corridas y auditoría. */
export function formatearFecha(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Convierte un valor de texto en una URL externa segura, o `null` si no se
 * puede enlazar.
 *
 * Resuelve dos problemas distintos:
 *
 * 1. **El enlace apuntaba al propio servidor.** El modelo devuelve los sitios
 *    web como dominios pelados —`bhp.com`, `ascc.cl`—, y un `href` sin esquema
 *    es una URL *relativa*: el navegador la resolvía contra la página actual y
 *    acababa en `http://mi-servidor/entidad/5/bhp.com`.
 *
 * 2. **`javascript:` como vector de XSS.** Este valor lo escribe el modelo, que
 *    el §8 manda tratar como entrada no confiable. Un `href="javascript:…"` se
 *    ejecuta al hacer clic, así que solo se aceptan `http` y `https`; cualquier
 *    otro esquema se rechaza y el valor se muestra como texto plano.
 */
export function urlExterna(valor: string | null | undefined): string | null {
  const texto = (valor ?? '').trim()
  if (!texto) return null

  // Un esquema explícito distinto de http(s) no se enlaza nunca.
  const conEsquema = /^([a-z][a-z0-9+.-]*):/i.exec(texto)
  if (conEsquema && !/^https?$/i.test(conEsquema[1])) return null

  const candidato = conEsquema ? texto : `https://${texto}`
  try {
    const url = new URL(candidato)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    // Sin host no hay sitio al que ir: `https://` a secas, por ejemplo.
    return url.hostname ? url.href : null
  } catch {
    return null
  }
}
